/**
 * CR-7353: NCR Escape root-cause chain switch.
 *
 * CR-7353 split an NCR's Root Cause Analysis into an Occurrence chain and an
 * Escape chain, each with its own Whys and a Selected Root Cause. This switch
 * decides whether a customer uses the Escape chain: the second 5-Whys section
 * on the NCR form, the "Occurrence" / "Escape" wording, the Escape Root Cause
 * register column and export, and whether the Escape fields are saved at all.
 *
 * Default is OFF — the form shows the single root-cause chain. Turning it off
 * hides the chain and stops it being saved; it does not delete anything
 * already entered.
 *
 * Stored in `feature_flags.ncr_escape_root_cause_enabled`. A Helix operator
 * writes the platform default; a client admin's save is routed to their own
 * tenant's override (see `core.admin_setup_settings`), and either way the
 * settings endpoint records who changed it and when.
 */
import { useState, useEffect } from 'react';
import { platformSettingsAPI } from '../../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Loader2, GitBranch } from 'lucide-react';
import { toast } from 'sonner';

export function EscapeRootCauseFeatureSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  // The full feature_flags dict is kept so a toggle save doesn't drop siblings.
  const [featureFlags, setFeatureFlags] = useState<Record<string, unknown>>({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await platformSettingsAPI.getSettings();
        const flags = data.feature_flags ?? {};
        setFeatureFlags(flags);
        setEnabled(!!flags.ncr_escape_root_cause_enabled);
      } catch (err: unknown) {
        console.error('Error loading Escape root cause setting:', err);
        toast.error('Failed to load the Escape root cause setting');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleToggle = async (checked: boolean) => {
    const previous = enabled;
    setEnabled(checked);
    setSaving(true);
    try {
      const nextFlags = { ...featureFlags, ncr_escape_root_cause_enabled: checked };
      await platformSettingsAPI.patchSettings({ feature_flags: nextFlags });
      setFeatureFlags(nextFlags);
      toast.success(`Escape root cause analysis: ${checked ? 'ON' : 'OFF'}`);
    } catch (err: unknown) {
      console.error('Error saving Escape root cause setting:', err);
      toast.error('Failed to save the Escape root cause setting');
      setEnabled(previous);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitBranch className="h-5 w-5" />
          NCR Escape Root Cause Analysis
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Splits an NCR&apos;s Root Cause Analysis into an Occurrence chain and an Escape
          chain, each with its own Whys and a Selected Root Cause. Controls the Escape section
          on the NCR form, the Escape Root Cause column in the NCR register and its export,
          and whether Escape answers are saved.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between rounded-md border border-input bg-background px-4 py-3">
          <div className="space-y-1 pr-3">
            <Label htmlFor="ncr-escape-root-cause-enabled" className="text-sm font-medium">
              Enable Escape Root Cause Analysis
            </Label>
            <p className="text-xs text-muted-foreground">
              {enabled
                ? 'ON — NCRs have an Occurrence and an Escape root cause chain.'
                : 'OFF — NCRs have a single root cause chain. Escape answers already entered are kept, not deleted.'}
            </p>
          </div>
          <Switch
            id="ncr-escape-root-cause-enabled"
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={saving}
          />
        </div>
      </CardContent>
    </Card>
  );
}
